import { lazy } from "react";
import { Navigate, Outlet, type RouteProps } from "react-router-dom";
import Dashboard from "@/pages/dashboard/page";
import Intelligence from "@/pages/intelligence/page";
import GnnStatus from "@/pages/gnn/page";
import AgenticCheckout from "@/pages/agentic-checkout/page";
import AnalyticsRedirect from "@/common/routes/AnalyticsRedirect";
// Plain data (the Analytics child list), not view code: the analytics views stay lazy below.
import { analyticsHomePath } from "@/pages/analytics/analyticsRoutes";

// Analytics views load lazily inside the analytics layout (ANL-E04-S02): a chunk loading there
// shows the layout's content skeleton, not the full-screen loader.
const AnalyticsLayout = lazy(() => import("@/pages/analytics/AnalyticsLayout"));
const ProductRecordsPage = lazy(
  () => import("@/pages/analytics/product/ProductRecordsPage")
);
const ProductOverviewTab = lazy(() => import("@/pages/analytics/product/tabs/OverviewTab"));
const ProductDiscoverTab = lazy(() => import("@/pages/analytics/product/tabs/DiscoverTab"));
const ProductEventsTab = lazy(() => import("@/pages/analytics/product/tabs/EventsTab"));
const ProductWishlistTab = lazy(() => import("@/pages/analytics/product/tabs/WishlistTab"));
const ProductEventProductsTab = lazy(
  () => import("@/pages/analytics/product/tabs/EventProductsTab")
);
const ProductGiftSplitsTab = lazy(() => import("@/pages/analytics/product/tabs/GiftSplitsTab"));
const ProductMobileTab = lazy(() => import("@/pages/analytics/product/tabs/MobileTab"));
const ProductFriendsTab = lazy(() => import("@/pages/analytics/product/tabs/FriendsTab"));
const ProductBadgesTab = lazy(() => import("@/pages/analytics/product/tabs/BadgesTab"));
const SignIn = lazy(() => import("@/pages/auth/sign-in/page"));
const ForgotPassword = lazy(() => import("@/pages/auth/forgot-password/page"));
const Holidays = lazy(() => import("@/pages/holidays/holidays"));
const AddHoliday = lazy(() => import("@/pages/holidays/components/addHoliday"));
const EditHoliday = lazy(
  () => import("@/pages/holidays/components/editHoliday")
);
const Users = lazy(() => import("@/pages/users/users"));
const UpdateUser = lazy(() => import("@/pages/users/components/updateUser"));
const UserDetail = lazy(() => import("@/pages/users/userDetail"));
const Orders = lazy(() => import("@/pages/orders/orders"));
const AffiliateOffers = lazy(() => import("@/pages/offers/page"));
const Brands = lazy(() => import("@/pages/brands/brands"));
const BrandsForm = lazy(() => import("@/pages/brands/components/brandForm"));
const AnnouncementForm = lazy(
  () => import("@/pages/announcements/components/announcementForm")
);
const GyftcoinSettingForm = lazy(
  () => import("@/pages/gyftcoin-setting/components/gyftcoinSettingForm")
);
const CompanyInfoForm = lazy(
  () => import("@/pages/company-info/components/companyInfoForm")
);
const Announcements = lazy(
  () => import("@/pages/announcements/announcements")
);
const BadgeForm = lazy(() => import("@/pages/badges/components/badgeForm"));
const Badges = lazy(() => import("@/pages/badges/badges"));
const CharityOrganizations = lazy(
  () => import("@/pages/charity-orginization/chaityOrganizations")
);
const CharityForm = lazy(
  () => import("@/pages/charity-orginization/components/charityForm")
);
const Feedbacks = lazy(() => import("@/pages/feedbacks/feedbacks"));
const CharityBookkeeping = lazy(
  () => import("@/pages/charity-bookkeeping/charityBookkeeping")
);
const LegalDocuments = lazy(() => import("@/pages/legal/legalDocuments"));
const TaxonomyCategories = lazy(() => import("@/pages/taxonomy/categories"));
const TaxonomyCategoryForm = lazy(() => import("@/pages/taxonomy/components/categoryForm"));
const TaxonomyCollections = lazy(() => import("@/pages/taxonomy/collections"));
const TaxonomyRelationships = lazy(
  () => import("@/pages/taxonomy/relationships")
);
const TaxonomyCollectionForm = lazy(
  () => import("@/pages/taxonomy/components/collectionForm")
);
const TaxonomyRelationshipForm = lazy(
  () => import("@/pages/taxonomy/components/relationshipForm")
);
const CommerceMediaCampaigns = lazy(
  () => import("@/pages/commerce-media/campaigns")
);
const CommerceMediaGuardrail = lazy(
  () => import("@/pages/commerce-media/guardrail")
);
const CommerceMediaSetup = lazy(() => import("@/pages/commerce-media/setup"));
const CommerceMediaBrandAccess = lazy(
  () => import("@/pages/commerce-media/brandAccess")
);
const CommerceMediaReports = lazy(
  () => import("@/pages/commerce-media/reports")
);

export type RoutesProps = {
  path?: RouteProps["path"];
  name: string;
  element: RouteProps["element"];
  exact?: boolean;
  /** Renders at the parent's own path; a route with `index` has no `path` or `children`. */
  index?: true;
  /** Nested routes, rendered by the parent element's `<Outlet>`. */
  children?: RoutesProps[];
};

const initialRoutes: RoutesProps[] = [
  {
    path: "/",
    name: "root",
    element: <Navigate to="/dashboard" />,
  },
];

const generalRoutes: RoutesProps[] = [
  {
    path: "/dashboard",
    name: "Dashboard",
    element: <Dashboard />,
  },
  {
    path: "/analytics",
    name: "Analytics",
    element: <AnalyticsLayout title="Analytics" />,
    children: [
      // `/analytics` lands on the first shipped child in sidebar order (ANL-E04-S03). Each child
      // registered here must be marked shipped in ANALYTICS_CHILDREN (analytics-menu.test.ts).
      { index: true, name: "Analytics Home", element: <AnalyticsRedirect to={analyticsHomePath()} /> },
      {
        path: "product",
        name: "Product records",
        // Only an outlet, so the redirect to the first tab never waits on Product records' data.
        element: <Outlet />,
        children: [
          { index: true, name: "Product records Home", element: <AnalyticsRedirect to="overview" /> },
          {
            name: "Product records Tabs",
            element: <ProductRecordsPage />,
            children: [
              // Slugs are the pre-split tab eventKeys, unchanged (OI-ANL-112); same order as
              // PRODUCT_RECORDS_TABS.
              { path: "overview", name: "Product records Overview", element: <ProductOverviewTab /> },
              { path: "discover", name: "Product records Discovery", element: <ProductDiscoverTab /> },
              { path: "events", name: "Product records Events", element: <ProductEventsTab /> },
              { path: "wishlist", name: "Product records Wishlist", element: <ProductWishlistTab /> },
              {
                path: "event-products",
                name: "Product records Event products",
                element: <ProductEventProductsTab />,
              },
              {
                path: "gift-splits",
                name: "Product records Gift splits",
                element: <ProductGiftSplitsTab />,
              },
              { path: "mobile", name: "Product records Mobile analytics", element: <ProductMobileTab /> },
              { path: "friends", name: "Product records Friends", element: <ProductFriendsTab /> },
              { path: "badges", name: "Product records Badges", element: <ProductBadgesTab /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/intelligence",
    name: "Intelligence",
    element: <Intelligence />,
  },
  {
    path: "/holidays",
    name: "Holidays",
    element: <Holidays />,
  },
  {
    path: "/holidays/add",
    name: "Holidays Add",
    element: <AddHoliday />,
  },
  {
    path: "/holidays/edit/:id",
    name: "Holidays Edit",
    element: <EditHoliday />,
  },

  {
    path: "/users",
    name: "User",
    element: <Users />,
  },
  {
    path: "/users/edit/:id",
    name: "User Edit",
    element: <UpdateUser />,
  },
  {
    path: "/users/:id",
    name: "User Detail",
    element: <UserDetail />,
  },

  {
    path: "/orders",
    name: "Orders",
    element: <Orders />,
  },
  {
    path: "/offers",
    name: "Affiliate offers",
    element: <AffiliateOffers />,
  },
  {
    path: "/brands",
    name: "Brands",
    element: <Brands />,
  },
  {
    path: "/brands/add",
    name: "Add Brand",
    element: <BrandsForm />,
  },
  {
    path: "/brands/edit/:id",
    name: "Add Brand",
    element: <BrandsForm />,
  },
  {
    path: "/announcements",
    name: "Announcement",
    element: <Announcements />,
  },
  {
    path: "/announcements/add",
    name: "Announcement add",
    element: <AnnouncementForm />,
  },
  {
    path: "/announcements/edit/:id",
    name: "Announcement edit",
    element: <AnnouncementForm />,
  },
  {
    path: "/badges",
    name: "Badges",
    element: <Badges />,
  },
  {
    path: "/badges/add",
    name: "Add Badges",
    element: <BadgeForm />,
  },
  {
    path: "/badges/edit/:id",
    name: "Edit Badges",
    element: <BadgeForm />,
  },
  {
    path: "/charity-organizations",
    name: "Charity Organizations",
    element: <CharityOrganizations />,
  },
  {
    path: "/charity-organizations/add",
    name: "Add Charity Organizations",
    element: <CharityForm />,
  },
  {
    path: "/charity-organizations/edit/:id",
    name: "Edit Charity Organizations",
    element: <CharityForm />,
  },
  {
    path: "/feedbacks",
    name: "Feedbacks",
    element: <Feedbacks />,
  },
  {
    path: "/gyftcoin-setting",
    name: "Gyftcoin Add",
    element: <GyftcoinSettingForm />,
  },
  {
    path: "/company-info",
    name: "Company Information",
    element: <CompanyInfoForm />,
  },
  {
    path: "/charity-bookkeeping",
    name: "Charity Bookkeeping",
    element: <CharityBookkeeping />,
  },
  {
    path: "/gnn",
    name: "GNN",
    element: <GnnStatus />,
  },
  {
    path: "/agentic-checkout",
    name: "Agentic Checkout",
    element: <AgenticCheckout />,
  },
  {
    path: "/commerce-media/campaigns",
    name: "Commerce Media",
    element: <CommerceMediaCampaigns />,
  },
  {
    path: "/commerce-media/guardrail",
    name: "Commerce Media Guardrail",
    element: <CommerceMediaGuardrail />,
  },
  {
    path: "/commerce-media/setup",
    name: "Commerce Media Setup",
    element: <CommerceMediaSetup />,
  },
  {
    path: "/commerce-media/brand-access",
    name: "Commerce Media Brand Access",
    element: <CommerceMediaBrandAccess />,
  },
  {
    path: "/commerce-media/reports",
    name: "Commerce Media Reports",
    element: <CommerceMediaReports />,
  },
  {
    path: "/legal-documents",
    name: "Legal Documents",
    element: <LegalDocuments />,
  },
  {
    path: "/taxonomy/collections",
    name: "Collections",
    element: <TaxonomyCollections />,
  },
  {
    path: "/taxonomy/collections/add",
    name: "Add Collection",
    element: <TaxonomyCollectionForm />,
  },
  {
    path: "/taxonomy/collections/edit/:id",
    name: "Edit Collection",
    element: <TaxonomyCollectionForm />,
  },
  {
    path: "/taxonomy/categories",
    name: "Categories",
    element: <TaxonomyCategories />,
  },
  {
    path: "/taxonomy/categories/add",
    name: "Add Category",
    element: <TaxonomyCategoryForm />,
  },
  {
    path: "/taxonomy/categories/edit/:id",
    name: "Edit Category",
    element: <TaxonomyCategoryForm />,
  },
  {
    path: "/taxonomy/relationships",
    name: "Relationships",
    element: <TaxonomyRelationships />,
  },
  {
    path: "/taxonomy/relationships/add",
    name: "Add Relationship",
    element: <TaxonomyRelationshipForm />,
  },
  {
    path: "/taxonomy/relationships/edit/:id",
    name: "Edit Relationship",
    element: <TaxonomyRelationshipForm />,
  },
];

export const getAuthRoutes = (): RoutesProps[] => [
  {
    name: "Sign In",
    path: "/sign-in",
    element: <SignIn />,
  },
  {
    name: "Forgot Password",
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
];

export const getAppRoutes = () => [...initialRoutes, ...generalRoutes];
